<template>
    <div
        :class="[{
                     'app-form-item--feedback': AppForm && AppForm.statusIcon,
                     'is-error': validateState === 'error',
                     'is-validating': validateState === 'validating',
                     'is-success': validateState === 'success',
                     'is-required': isRequired || required,
                     'is-no-asterisk': AppForm && AppForm.hideRequiredAsterisk
                 },
                 sizeClass ? 'app-form-item--' + sizeClass : ''
        ]"
        class="app-form-item">
        <!-- <label-wrap
            :is-auto-width="labelStyle && labelStyle.width === 'auto'"
            :update-all="form.labelWidth === 'auto'"
        > -->
        <label
            v-if="label || $slots.label"
            :for="labelFor"
            :style="labelStyle"
            class="app-form-item__label">
            <slot name="label">{{ label + form.labelSuffix }}</slot>
        </label>
        <!-- </label-wrap> -->
        <div :style="contentStyle" class="app-form-item__content">
            <slot />
            <transition name="el-zoom-in-top">
                <slot
                    v-if="validateState === 'error' && showMessage && form.showMessage"
                    :error="validateMessage"
                    name="error">
                    <div
                        :class="{
                            'app-form-item__error--inline': typeof inlineMessage === 'boolean'
                                ? inlineMessage
                                : (AppForm && AppForm.inlineMessage || false)
                        }"
                        class="app-form-item__error">
                        {{ validateMessage }}
                    </div>
                </slot>
            </transition>
        </div>
    </div>
</template>

<script>
import AsyncValidator from 'async-validator'
import emitter from '../../utilities/emitter'
import objectAssign from '../../utilities/merge'
import LabelWrap from './_FormLabel'

export default {
    name: 'app-form-item',
    components: {
        // use this component to calculate auto width
        LabelWrap
    },
    mixins: [emitter],
    provide() {
        return {
            AppFormItem: this
        }
    },
    inject: ['AppForm'],
    props: {
        label: String,
        labelWidth: String,
        prop: String,
        required: {
            type: Boolean,
            default: undefined
        },
        rules: [Object, Array],
        error: String,
        validateStatus: String,
        for: String,
        inlineMessage: {
            type: [String, Boolean],
            default: ''
        },
        showMessage: {
            type: Boolean,
            default: true
        },
        size: String
    },
    data() {
        return {
            validateState: '',
            validateMessage: '',
            validateDisabled: false,
            validator: {},
            isNested: false,
            computedLabelWidth: ''
        }
    },
    computed: {
        labelFor() {
            return this.for || this.prop
        },
        labelStyle() {
            const ret = {}
            if (this.form.labelPosition === 'top') return ret
            const labelWidth = this.labelWidth || this.form.labelWidth
            if (labelWidth) {
                ret.width = labelWidth
            }
            return ret
        },
        contentStyle() {
            const ret = {}
            const label = this.label
            if (this.form.labelPosition === 'top' || this.form.inline)
                return ret
            if (!label && !this.labelWidth && this.isNested) return ret
            const labelWidth = this.labelWidth || this.form.labelWidth
            if (labelWidth === 'auto') {
                if (this.labelWidth === 'auto') {
                    ret.marginLeft = this.computedLabelWidth
                }
                else if (this.form.labelWidth === 'auto') {
                    ret.marginLeft = this.appForm.autoLabelWidth
                }
            }
            else {
                ret.marginLeft = labelWidth
            }
            return ret
        },
        form() {
            let parent = this.$parent
            let parentName = parent.$options.name
            while (parentName !== 'app-form') {
                if (parentName === 'app-form-item') {
                    // eslint-disable-next-line
                    this.isNested = true
                }
                parent = parent.$parent
                parentName = parent.$options.name
            }
            return parent
        },
        fieldValue() {
            const model = this.form.model
            if (!model || !this.prop) {
                return
            }

            let path = this.prop
            if (path.indexOf(':') !== -1) {
                path = path.replace(/:/, '.')
            }
            return this.getPropByPath(model, path, true).v
        },
        isRequired() {
            let rules = this.getRules()
            let isRequired = false

            if (rules && rules.length) {
                rules.every(rule => {
                    if (rule.required) {
                        isRequired = true
                        return false
                    }
                    return true
                })
            }
            return isRequired
        },
        _formSize() {
            return this.AppForm ? this.AppForm.size : null
        },
        appFormItemSize() {
            return this.size || this._formSize
        },
        sizeClass() {
            return this.appFormItemSize || (this.$ELEMENT || {}).size
        }
    },
    mounted() {
        if (this.prop) {
            this.dispatch('app-form', 'app.form.addField', [this])

            let initialValue = this.fieldValue
            if (Array.isArray(initialValue)) {
                initialValue = [].concat(initialValue)
            }
            Object.defineProperty(this, 'initialValue', {
                value: initialValue
            })

            let rules = this.getRules()

            if (rules.length || this.required !== undefined) {
                this.$on('app.form.blur', this.onFieldBlur)
                this.$on('app.form.change', this.onFieldChange)
            }
        }
    },
    beforeDestroy() {
        this.dispatch('app-form', 'app.form.removeField', [this])
    },
    methods: {
        noop() { },
        getPropByPath(obj, path, strict) {
            let tempObj = obj;
            path = path.replace(/\[(\w+)\]/g, '.$1');
            path = path.replace(/^\./, '');

            let keyArr = path.split('.');
            let i = 0;
            for (let len = keyArr.length; i < len - 1; ++i) {
                if (!tempObj && !strict) break;
                let key = keyArr[i];
                if (key in tempObj) {
                    tempObj = tempObj[key];
                }
                else {
                    if (strict) {
                        throw new Error('please transfer a valid prop path to form item!');
                    }
                    break;
                }
            }
            return {
                o: tempObj,
                k: keyArr[i],
                v: tempObj ? tempObj[keyArr[i]] : null
            }
        },
        validate(trigger, callback = this.noop) {
            this.validateDisabled = false
            const rules = this.getFilteredRule(trigger)
            if ((!rules || rules.length === 0) && this.required === undefined) {
                callback()
                return true
            }

            this.validateState = 'validating'

            const descriptor = {}
            if (rules && rules.length > 0) {
                rules.forEach(rule => {
                    delete rule.trigger
                })
            }
            descriptor[this.prop] = rules

            const validator = new AsyncValidator(descriptor)
            const model = {}

            model[this.prop] = this.fieldValue

            validator.validate(model,
                { firstFields: true },
                (errors, invalidFields) => {
                    this.validateState = !errors ? 'success' : 'error'
                    this.validateMessage = errors ? errors[0].message : ''

                    callback(this.validateMessage, invalidFields)
                    this.elForm &&
                        this.appForm.$emit(
                            'validate',
                            this.prop,
                            !errors,
                            this.validateMessage || null
                        )
                })
        },
        clearValidate() {
            this.validateState = ''
            this.validateMessage = ''
            this.validateDisabled = false
        },
        resetField() {
            this.validateState = ''
            this.validateMessage = ''

            let model = this.form.model
            let value = this.fieldValue
            let path = this.prop
            if (path.indexOf(':') !== -1) {
                path = path.replace(/:/, '.')
            }

            let prop = this.getPropByPath(model, path, true)

            this.validateDisabled = true
            if (Array.isArray(value)) {
                prop.o[prop.k] = [].concat(this.initialValue)
            }
            else {
                prop.o[prop.k] = this.initialValue
            }

            this.broadcast('ElTimeSelect', 'fieldReset', this.initialValue)
        },
        getRules() {
            let formRules = this.form.rules
            const selfRules = this.rules
            const requiredRule =
                this.required !== undefined ? { required: !!this.required } : []

            const prop = this.getPropByPath(formRules, this.prop || '')
            formRules = formRules ? prop.o[this.prop || ''] || prop.v : []

            return [].concat(selfRules || formRules || []).concat(requiredRule)
        },
        getFilteredRule(trigger) {
            const rules = this.getRules()

            return rules
                .filter(rule => {
                    if (!rule.trigger || trigger === '') return true
                    if (Array.isArray(rule.trigger)) {
                        return rule.trigger.indexOf(trigger) > -1
                    }
                    else {
                        return rule.trigger === trigger
                    }
                })
                .map(rule => objectAssign({}, rule))
        },
        onFieldBlur() {
            this.validate('blur')
        },
        onFieldChange() {
            if (this.validateDisabled) {
                this.validateDisabled = false
                return
            }

            this.validate('change')
        },
        updateComputedLabelWidth(width) {
            this.computedLabelWidth = width ? `${width}px` : ''
        }
    },
    watch: {
        error: {
            immediate: true,
            handler(value) {
                this.validateMessage = value
                this.validateState = value ? 'error' : ''
            }
        },
        validateStatus(value) {
            this.validateState = value
        }
    },
}
</script>

<style lang="stylus">
    .app-form-item
        margin-bottom: 15px
        &:after,
        &:before
            display: table
            content: ""
    
    .app-form-item__label
        vertical-align: middle 
        float: left
        font-size: 14px
        color: #606266
        -webkit-box-sizing: border-box
        box-sizing: border-box
        padding: 5px 12px 5px 0
        line-height: 16px
        width: 100%
        text-align: left
        position: relative
        z-index: 2

    .app-form-item__content
        line-height: 40px
        position: relative
        font-size: 14px
        display: block

    .app-form-item__error
        color: #F56C6C
        font-size: 12px
        line-height: 1
        padding-top: 4px
        position: absolute
        top: 100%
        left: 0
    
    .app-form-item.is-required:not(.is-no-asterisk) .app-form-item__label:before
        content: "*"
        color: #f56c6c
        margin-right: 4px

</style>
